import { Component } from './component.js';
import { debounce, isClickedOutside, onAnimationEnd } from './utilities.js';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);

      // Add touch listeners for mobile swipe-to-close
      if (window.innerWidth < 750) {
        this.refs.dialog.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
        this.refs.dialog.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
        this.refs.dialog.addEventListener('touchend', this.#handleTouchEnd);
      }
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.refs.dialog.removeEventListener('touchstart', this.#handleTouchStart);
    this.refs.dialog.removeEventListener('touchmove', this.#handleTouchMove);
    this.refs.dialog.removeEventListener('touchend', this.#handleTouchEnd);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  // Touch handling for mobile swipe-to-close
  #startY = 0;
  #currentY = 0;
  #isDragging = false;

  #handleTouchStart = (event) => {
    const dialog = this.refs.dialog;
    if (dialog && dialog.scrollTop > 0) return;

    this.#startY = event.touches[0].clientY;
    this.#currentY = this.#startY;
    this.#isDragging = true;
    dialog.style.transition = 'none';
  };

  #handleTouchMove = (event) => {
    if (!this.#isDragging) return;

    const dialog = this.refs.dialog;
    if (dialog && dialog.scrollTop > 0) {
      this.#isDragging = false;
      dialog.style.transform = '';
      return;
    }

    this.#currentY = event.touches[0].clientY;
    const deltaY = this.#currentY - this.#startY;

    if (deltaY > 0) {
      if (event.cancelable) event.preventDefault();
      dialog.style.transform = `translateY(${deltaY}px)`;
    }
  };

  #handleTouchEnd = async () => {
    if (!this.#isDragging) return;
    this.#isDragging = false;

    const dialog = this.refs.dialog;
    const deltaY = this.#currentY - this.#startY;

    if (deltaY > 100) {
      dialog.style.transform = '';
      dialog.style.setProperty('--start-y', `${deltaY}px`);
      await this.closeDialog();
      dialog.style.removeProperty('--start-y');
    } else {
      dialog.style.transition = 'transform 0.3s ease-in-out';
      dialog.style.transform = '';

      setTimeout(() => {
        if (dialog) dialog.style.transition = '';
      }, 300);
    }
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
